import { useState } from "react";
import { playNow, kickAudio } from "@/audio/piano";
import { spellScale, parseNote } from "@/theory/spell";
import { triadPcs, voicing, pretty } from "@/theory/notes";

/**
 * ChordTree — the diatonic chord tree of a major key.
 *
 * Renders the 7 chords of the chosen major scale with their roman-numeral
 * roles (I, ii, iii, IV, V, vi, vii°) and qualities. Click any chord to
 * hear it. The "trunk" colours the major / minor / diminished groups.
 *
 * Used in lessons 26-29 (Chord Trees), 30 (Chord Charts), 31 (Roman Numeral
 * Analysis), and informally referenced in chord-lessons throughout.
 */

const MAJOR_INTERVALS = [0, 2, 4, 5, 7, 9, 11];
const ROMAN_MAJOR = ["I", "ii", "iii", "IV", "V", "vi", "vii°"];
const QUALITY_MAJOR: ("M" | "m" | "dim")[] = ["M", "m", "m", "M", "M", "m", "dim"];
// The diatonic minor counterparts (natural minor → 1=i, 2=ii°, 3=III, 4=iv, 5=v, 6=VI, 7=VII)
const ROMAN_MINOR = ["i", "ii°", "III", "iv", "v", "VI", "VII"];
const QUALITY_MINOR: ("M" | "m" | "dim")[] = ["m", "dim", "M", "m", "m", "M", "M"];
const MINOR_INTERVALS = [0, 2, 3, 5, 7, 8, 10];

/** Degree labels, so each chord root is spelled by its letter rather than
 *  looked up by pitch class — C minor's III is E♭, never D♯. */
const MAJOR_DEGREES = ["1", "2", "3", "4", "5", "6", "7"];
const MINOR_DEGREES = ["1", "2", "♭3", "4", "5", "♭6", "♭7"];

type Mode = "major" | "minor";

const ROOTS = ["C", "G", "D", "A", "E", "B", "F", "Bb", "Eb", "Ab"];

interface ChordTreeProps {
  tonic?: string;
  mode?: Mode;
}

export function ChordTree({ tonic: initialTonic = "C", mode: initialMode = "major" }: ChordTreeProps) {
  const [tonic, setTonic] = useState(initialTonic);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [hovered, setHovered] = useState<number | null>(null);

  const tonicSemi = parseNote(tonic)?.pc ?? 0;

  const intervals = mode === "major" ? MAJOR_INTERVALS : MINOR_INTERVALS;
  const romans = mode === "major" ? ROMAN_MAJOR : ROMAN_MINOR;
  const qualities = mode === "major" ? QUALITY_MAJOR : QUALITY_MINOR;
  const degrees = mode === "major" ? MAJOR_DEGREES : MINOR_DEGREES;
  // Roots spelled by degree, ASCII so they can seed the triad speller.
  const rootNames = spellScale(tonic, degrees, true);

  const chordAt = (deg: number) => {
    const rootSemi = (tonicSemi + intervals[deg]) % 12;
    const q = qualities[deg];
    const root = rootNames[deg];
    const quality = q === "M" ? "major" : q === "m" ? "minor" : "diminished";
    // Every chord tone spelled from its own root: B° is B–D–F, D° is D–F–A♭.
    const pitches = voicing(triadPcs(root, quality), 4);
    return { rootName: pretty(root), rootSemi, q, pitches };
  };

  const playChord = (deg: number) => {
    const c = chordAt(deg);
    kickAudio();
    void playNow(c.pitches, "2n", 0.7);
  };

  const qualityColor = (q: "M" | "m" | "dim") =>
    q === "M" ? "emerald" : q === "m" ? "rose" : "amber";

  return (
    <div className="my-5 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.05] to-purple-500/[0.03] p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">
            Diatonic chord tree
          </div>
          <div className="mt-1 font-mt text-xl text-nsm-ink">
            <span className="font-semibold text-emerald-300">{tonic} {mode === "major" ? "major" : "minor"}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMode("major")}
              className={`rounded px-2 py-1 text-[11px] font-semibold uppercase tracking-wider transition ${
                mode === "major" ? "bg-emerald-500/30 text-emerald-100" : "bg-zinc-800/50 text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              Major
            </button>
            <button
              onClick={() => setMode("minor")}
              className={`rounded px-2 py-1 text-[11px] font-semibold uppercase tracking-wider transition ${
                mode === "minor" ? "bg-rose-500/30 text-rose-100" : "bg-zinc-800/50 text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              Minor
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-1">
        <span className="mr-1 text-[10px] uppercase tracking-wider text-nsm-dim">Tonic:</span>
        {ROOTS.map((r) => (
          <button
            key={r}
            onClick={() => setTonic(r)}
            className={`min-w-[32px] rounded px-2 py-1.5 text-[12px] font-semibold transition ${
              r === tonic ? "bg-amber-500/30 text-amber-100" : "bg-zinc-800/50 text-nsm-dim hover:text-nsm-ink"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      {/* The tree — 7 chord cards in a row with branching lines */}
      <div className="grid grid-cols-7 gap-2">
        {romans.map((rom, i) => {
          const c = chordAt(i);
          const color = qualityColor(c.q);
          const colorClasses: Record<string, string> = {
            emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-200",
            rose:    "border-rose-500/40    bg-rose-500/5    text-rose-200",
            amber:   "border-amber-500/40   bg-amber-500/5   text-amber-200",
          };
          const isHov = hovered === i;
          return (
            <button
              key={i}
              onClick={() => playChord(i)}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`flex flex-col items-center gap-1 rounded-xl border p-3 transition ${colorClasses[color]} ${
                isHov ? "scale-[1.04] shadow-lg" : ""
              }`}
            >
              <span className="font-mt text-lg font-bold text-nsm-ink">
                {c.rootName}{c.q === "m" ? "m" : c.q === "dim" ? "°" : ""}
              </span>
              <span className="text-[10px] uppercase tracking-wider opacity-80">{rom}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-nsm-dim">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-emerald-400" /> major
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-rose-400" /> minor
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-amber-400" /> diminished
        </span>
        <span className="ml-auto">Click any card to hear the chord.</span>
      </div>
    </div>
  );
}
